/**
 * A vendor's recommender reward program for one listing (docs/Decisions.md
 * D68 item 4, D73): a percentage of the lead fee, a flat amount per won
 * deal, credits with the vendor, attribution only, or none. Anyone may
 * recommend the vendor whatever the program; money rewards come out of the
 * lead fee and show as "May earn a referral reward" (CR-36).
 */
import { useState } from 'react';
import * as marketApi from '../api/marketplace';
import { useToast } from './toast';
import { Field, SelectField, TextareaField } from './ui';
import {
  REWARD_CREDITS_MAX,
  REWARD_TERMS_MAX,
  REWARD_TYPES,
  REWARD_TYPE_LABELS,
  amountToCents,
  centsToInput,
  rewardProgramLabel,
} from '../lib/marketplace';
import { fieldErrorsFrom } from '../lib/formErrors';
import { cn, ui } from '../lib/ui';

export default function RewardProgramEditor({ listing, onSaved }) {
  const toast = useToast();
  const current = listing.rewardProgram;
  const [form, setForm] = useState({
    type: current?.type || 'none',
    percent: current?.percent != null ? String(current.percent) : '',
    amount: centsToInput(current?.amountCents),
    credits: current?.credits || '',
    terms: current?.terms || '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors({});
  };

  async function save(e) {
    e.preventDefault();
    const body = { type: form.type };
    if (form.type === 'percent') body.percent = Number(form.percent);
    if (form.type === 'flat') {
      const cents = amountToCents(form.amount);
      if (Number.isNaN(cents)) {
        setErrors({ amountCents: 'Enter an amount such as 25 or 25.00' });
        return;
      }
      body.amountCents = cents;
    }
    if (form.type === 'credits') body.credits = form.credits;
    if (form.type !== 'none' && form.terms.trim()) body.terms = form.terms.trim();
    setSaving(true);
    try {
      const data = await marketApi.setRewardProgram(listing.listingId, body);
      toast.success(data.rewardProgram ? 'Reward program saved' : 'Reward program removed');
      onSaved?.(data.rewardProgram);
    } catch (err) {
      setErrors(fieldErrorsFrom(err));
      toast.error(err.message || 'Not saved');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={save} className={cn(ui.cardFlat, 'mb-4')} noValidate>
      <h3 className="mt-0 text-title-s">Recommender rewards</h3>
      <p className={cn(ui.hint, 'mt-0')}>
        Members can recommend you whatever you choose here; this decides what a recommendation that
        brings a won deal earns. Money rewards come out of the lead fee, before the
        salesperson&apos;s balance.
      </p>
      <SelectField
        label="Program"
        value={form.type}
        onChange={e => set('type', e.target.value)}
        error={errors.type}
      >
        <option value="none">No program</option>
        {REWARD_TYPES.map(t => (
          <option key={t} value={t}>
            {REWARD_TYPE_LABELS[t]}
          </option>
        ))}
      </SelectField>
      {form.type === 'percent' && (
        <Field
          label="Percent of the lead fee"
          inputMode="decimal"
          value={form.percent}
          onChange={e => set('percent', e.target.value)}
          error={errors.percent}
        />
      )}
      {form.type === 'flat' && (
        <Field
          label="Amount per won deal (USD)"
          inputMode="decimal"
          value={form.amount}
          onChange={e => set('amount', e.target.value)}
          error={errors.amountCents}
        />
      )}
      {form.type === 'credits' && (
        <Field
          label="What a recommender receives"
          placeholder="For example: a free annual policy review"
          maxLength={REWARD_CREDITS_MAX}
          value={form.credits}
          onChange={e => set('credits', e.target.value)}
          error={errors.credits}
        />
      )}
      {form.type !== 'none' && (
        <TextareaField
          label="Program terms (optional)"
          rows={2}
          maxLength={REWARD_TERMS_MAX}
          value={form.terms}
          onChange={e => set('terms', e.target.value)}
          error={errors.terms}
        />
      )}
      {current && <p className={ui.hint}>Now: {rewardProgramLabel(current)}</p>}
      <button type="submit" className={cn(ui.btnPrimary, ui.btnSm)} disabled={saving}>
        {saving ? 'Saving...' : 'Save reward program'}
      </button>
    </form>
  );
}
